import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const playDing = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    // two-tone chime
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t0 = now + i * 0.15;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.45);
      osc.start(t0);
      osc.stop(t0 + 0.5);
    });
  } catch (err) {
    console.warn("playDing failed:", err);
  }
};

const desktopNotify = (title, body) => {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const n = new Notification(title, {
      body,
      icon: "https://customer-assets.emergentagent.com/job_ice-commerce-pro-1/artifacts/yuxs0x8v_482327252_2947912865373631_5445166017531657186_n.jpg",
      tag: "easyice-order",
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch (err) {
    console.warn("desktopNotify failed:", err);
  }
};

const setHomeBadge = (count) => {
  try {
    if (typeof navigator !== "undefined" && "setAppBadge" in navigator) {
      if (count > 0) navigator.setAppBadge(count);
      else navigator.clearAppBadge?.();
    }
  } catch (err) {
    // unsupported / permission denied — silently ignore
  }
};

const NotificationsBell = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission === "granted"
      : false
  );
  const nav = useNavigate();
  const timer = useRef(null);
  const seenIds = useRef(new Set());
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      const items = r.data.items || [];
      if (firstLoad.current) {
        items.forEach((n) => seenIds.current.add(n.notification_id));
        firstLoad.current = false;
      } else {
        // Detect new items since last poll (only for admins, only unread)
        const fresh = items.filter(
          (n) => !seenIds.current.has(n.notification_id) && !n.read
        );
        if (fresh.length > 0 && (user?.role === "admin" || user?.role === "staff")) {
          playDing();
          const top = fresh[0];
          desktopNotify(top.title, top.body);
        }
        items.forEach((n) => seenIds.current.add(n.notification_id));
      }
      setData(r.data);
      setHomeBadge(r.data.unread || 0);
    } catch (err) {
      console.warn("notifications load failed:", err);
    }
  }, [user]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
  }, [load]);

  const enableAlerts = async () => {
    if (!("Notification" in window)) return;
    const perm = await Notification.requestPermission();
    setAlertsEnabled(perm === "granted");
    if (perm === "granted") {
      desktopNotify("Notifiche EasyIce attive", "Riceverai un avviso per ogni nuovo ordine.");
      playDing();
    }
  };

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const clickItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.notification_id}/read`);
    setOpen(false);
    load();
    if (n.linked_order_id && (user?.role === "admin" || user?.role === "staff")) nav("/admin");
  };

  const canEnableAlerts =
    (user?.role === "admin" || user?.role === "staff") &&
    typeof window !== "undefined" &&
    "Notification" in window &&
    !alertsEnabled;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative h-10 w-10 grid place-items-center rounded-lg hover:bg-sky-50 transition-colors"
          data-testid="notifications-bell"
          aria-label="Notifiche"
        >
          <Bell className="w-5 h-5 text-[#032B44]" />
          {data.unread > 0 && (
            <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" data-testid="notifications-badge" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 border-sky-100" data-testid="notifications-dropdown">
        <div className="px-4 py-3 border-b border-sky-100 flex items-center justify-between">
          <p className="font-heading text-sm font-semibold text-[#032B44]">Notifiche</p>
          {data.unread > 0 && (
            <button onClick={readAll} className="text-xs text-sky-700 hover:text-[#00A3FF] inline-flex items-center gap-1" data-testid="notifications-read-all">
              <Check className="w-3 h-3" /> Segna tutte
            </button>
          )}
        </div>
        {canEnableAlerts && (
          <button
            onClick={enableAlerts}
            className="w-full px-4 py-2.5 text-left text-xs bg-sky-50/60 border-b border-sky-100 hover:bg-sky-100/60 transition-colors"
            data-testid="enable-alerts-button"
          >
            <span className="font-medium text-[#032B44]">🔔 Attiva suono e avvisi desktop</span>
            <span className="block text-slate-500 mt-0.5">Ricevi un "ding" e una notifica del browser ad ogni nuovo ordine.</span>
          </button>
        )}
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 ? (
            <p className="p-8 text-center text-sm text-slate-500" data-testid="notifications-empty">Nessuna notifica.</p>
          ) : (
            data.items.map((n) => (
              <button
                key={n.notification_id}
                onClick={() => clickItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-sky-50 last:border-0 hover:bg-sky-50/60 transition-colors ${!n.read ? "bg-sky-50/40" : ""}`}
                data-testid={`notification-${n.notification_id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#00A3FF] mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#032B44] truncate">{n.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5">{n.body}</p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {new Date(n.created_at).toLocaleString("it-IT")}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsBell;
